<h1 class="text-center my-2">Create your Wallet</h1>

<div class="d-flex justify-content-center">
  
  <mat-vertical-stepper class="reg-stepper" #stepper [linear]="true" [formGroup]="registrationForm">
    <!-- Step 1: Who are you registering -->
    <mat-step [completed]="registrationForm.get('registeringAs')?.valid || registrationForm.get('registeringAs')?.disabled">
      <ng-template matStepLabel>Account Type</ng-template>
      <h4>I am</h4>
      <p>We offer services to both individuals and companies. Please choose one so that we can serve you better</p>
      <mat-radio-group formControlName="registeringAs">
        <mat-radio-button value="individual">An individual</mat-radio-button>
        <mat-radio-button value="company">A company</mat-radio-button>
      </mat-radio-group>
      <div>
       <button mat-button matStepperNext [disabled]="registrationForm.get('registeringAs')?.invalid">Next</button>
      </div>
    </mat-step>

    <!-- Step 2: Personal details -->
    <mat-step [completed]="isUserDetailsValid()">
      <ng-template matStepLabel>You</ng-template>
      <h4>Your details</h4>
      <p>Please provide your details</p>
      <ng-container formGroupName="personalDetails">
        @if (isCompanySelected()) {
          <div class="company-search-container">
            <div class="company-field-row">
              <mat-form-field class="company-input-field" subscriptSizing="dynamic" [floatLabel]="'always'" appearance="outline">
                <mat-label>Company Name</mat-label>
                @if (companyEditMode) {
                  <input matInput 
                        formControlName="companyName" 
                        placeholder="Type 3 characters to search"
                        [matAutocomplete]="companyAuto"/>
                } @else {
                  <input matInput 
                        formControlName="companyName" 
                        placeholder="Type 3 characters to search"/>
                }
                
                <!-- Clear button inside the input field -->
                @if (canShowCompanyEditButton()) {
                  <button matSuffix 
                          mat-icon-button 
                          type="button"
                          (click)="enableCompanyEditMode()"
                          matTooltip="Change company selection"
                          aria-label="Clear company selection">
                    <mat-icon>close</mat-icon>
                  </button>
                }
                
                <mat-autocomplete #companyAuto="matAutocomplete" 
                                  [displayWith]="displayCompanyName"
                                  (optionSelected)="onCompanySelected($event.option.value)">
                  @if (fetchingCompanyDetails && companyEditMode) {
                    <mat-option style="padding-left: 40%;">
                      <mat-spinner diameter="30"></mat-spinner>
                    </mat-option>
                  }
                  @if (companyEditMode) {
                    @for (company of filteredCompanies$ | async; track company.id) {
                      <mat-option [value]="company">{{company.name}}</mat-option>
                    }
                  }
                </mat-autocomplete>
                @if(registrationForm.get('personalDetails.companyName')?.errors?.['required']) {
                  <mat-error>Company name is required</mat-error>
                }
                @if(registrationForm.get('personalDetails.companyName')?.errors?.['invalidCompany']) {
                  <mat-error>Please select a valid company from the dropdown</mat-error>
                }
              </mat-form-field>
              
              <!-- Can't find your company button -->
              @if (companyEditMode) {
                <div class="cant-find-button-container">
                  <button mat-button 
                          type="button"
                          color="primary" 
                          class="cant-find-company-btn"
                          (click)="showAddCompanyDialog()">
                    Can't find your company?
                  </button>
                </div>
              }
            </div>
          </div>
          
          <!-- Typed company not found suggestion - separate container for next line -->
          @if (companyEditMode && typedCompanyName() && !isTypedCompanyInList() && !fetchingCompanyDetails) {
            <div class="company-suggestion-container">
              <div class="suggestion-content">
                <mat-icon class="suggestion-icon" color="accent">info</mat-icon>
                <div class="suggestion-text">
                  <p class="company-not-found-message">
                    <strong>{{ typedCompanyName() }}</strong> is not in our company database.
                  </p>
                  <p class="suggestion-hint">You can add it manually below.</p>
                </div>
              </div>
              <button mat-flat-button 
                      type="button"
                      color="primary" 
                      class="add-specific-company-btn"
                      (click)="showAddCompanyDialog()">
                <mat-icon>add</mat-icon>
                Add {{ typedCompanyName() }}
              </button>
            </div>
          }
        }
        <div class="user-details-container mb-4">
          <!-- Company Name and Role fields - only shown when registering as company -->
          @if (isCompanySelected()) {
            
            <!-- Contact Selection Dropdown -->
            <mat-form-field class="input-field my-sm-4" appearance="outline">
              <mat-label>Contact</mat-label>
              <mat-select formControlName="contactId" 
                         [placeholder]="selectedCompany ? 'Select a contact' : 'Select a company first'"
                         (selectionChange)="onContactSelected($event.value)">
                @if (fetchingCompanyDetails) {
                  <mat-option disabled>
                    <mat-spinner diameter="20"></mat-spinner>
                    Loading contacts...
                  </mat-option>
                }
                @for (contact of availableContacts; track contact.id) {
                  <mat-option [value]="contact.id">
                    {{ contact.displayName }}
                  </mat-option>
                }
              </mat-select>
              @if (selectedCompany) {
                <mat-hint>Choose an existing contact or select "Add new contact" to enter new details</mat-hint>
              } @else {
                <mat-hint>Please select a company first to view available contacts</mat-hint>
              }
            </mat-form-field>
            
            <mat-form-field class="input-field my-sm-4" appearance="outline">
              <mat-label>Role</mat-label>
              <input matInput formControlName="role" placeholder="ex. Director"/>
              @if(registrationForm.get('personalDetails.role')?.errors?.['required']) {
                <mat-error>Role is required</mat-error>
              }
            </mat-form-field>
          }
          
          <mat-form-field class="input-field" appearance="outline">
            <mat-label>First name</mat-label>
            <input matInput formControlName="firstName" placeholder="ex. John"/>
            @if(registrationForm.get('personalDetails.firstName')?.errors?.['required']) {
              <mat-error>First name is required</mat-error>
            }
          </mat-form-field>
          <mat-form-field class="input-field" appearance="outline">
            <mat-label>Last name</mat-label>
            <input matInput formControlName="lastName" placeholder="ex. Doe" />
            @if(registrationForm.get('personalDetails.lastName')?.errors?.['required']) {
              <mat-error>Last name is required</mat-error>
            }
          </mat-form-field>
          <mat-form-field class="input-field" appearance="outline">
            <mat-label>Email</mat-label>
            <input matInput formControlName="email" type="email" placeholder="ex. john.doe@email.com" />
            @if(registrationForm.get('personalDetails.email')?.errors?.['required']) {
              <mat-error>Email is required</mat-error>
            }
            @if(registrationForm.get('personalDetails.email')?.errors?.['email']) {
              <mat-error>Invalid email address</mat-error>
            }
            @if (isCompanySelected()) {
              <mat-hint>Please use an email associated with your organisation</mat-hint>
            }
          </mat-form-field>

          <mat-form-field class="input-field" appearance="outline">
            <mat-label>Phone number (optional)</mat-label>
            <input matInput formControlName="phone" placeholder="ex. 07876543210 or +447876543210"/>
            @if(registrationForm.get('personalDetails.phone')?.errors?.['pattern']) {
              <mat-error>Invalid phone number</mat-error>
            }
          </mat-form-field>
        </div>
      </ng-container>

      <!-- Email domain validation progress bar -->
      @if (validatingEmailDomain) {
        <div class="validation-section my-3">
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
          <p class="text-center mt-2">Validating your details...</p>
        </div>
      }

      <!-- Email domain validation warning -->
      @if (showDomainWarning && emailDomainValidation && !emailDomainValidation.isValid) {
        <div class="validation-warning my-3 p-3" style="background-color: #fff3cd; border: 1px solid #ffeaa7; border-radius: 4px;">
          <mat-icon style="color: #856404; vertical-align: middle; margin-right: 8px;" class="icon-no-shrink">warning</mat-icon>
          <span style="color: #856404;">{{ emailDomainValidation.message }}</span>
        </div>
      }
  
      <div>
        <button mat-button matStepperPrevious>Back</button>
        <button mat-button 
                [disabled]="!canProceedToNextStep()" 
                (click)="validateEmailDomainWithCompany()">
          {{ isCompanySelected() ? 'Validate and Continue' : 'Next' }}
        </button>
        @if (showDomainWarning) {
          <button mat-button color="primary" (click)="proceedToNextStep()" [disabled]="!canProceedToNextStep()">Continue Anyway</button>
        }
      </div>
    </mat-step>
  
    <!-- Step 3: Terms and conditions -->
    <mat-step [completed]="registrationForm.get('agreeToTerms')?.valid">
      <ng-template matStepLabel>Agreement</ng-template>

      <mat-checkbox formControlName="agreeToTerms1">
        I confirm that I have read and agree to the following:
      </mat-checkbox>
      <div class="terms-text">
      a) for Perse to provide me with a ConsentWallet account, being a direct contract between us for a free service that enables me to receive my energy data;
      <br/>
      b) the <a href="https://perse.io/terms" target="_blank">terms and conditions</a>; and
      <br/>
      c) the <a href="https://perse.io/privacy-policy" target="_blank">privacy policy</a>;

      </div>
      <div class="footer-section">
      @if (!registrationInProgress) {
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary" [disabled]="!allStepsValid()" (click)="onSubmit()">Submit</button>
      }
      @if (registrationInProgress) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      }
      </div>
    </mat-step>

  </mat-vertical-stepper>

  <!-- <button mat-raised-button color="primary" class="ml-2" (click)="onTest()">Test</button> -->
</div>

